import React from 'react'
import { Box, Button, Paper, Typography } from '@mui/material'
import AssignmentIcon from '@mui/icons-material/Assignment'
import DescriptionIcon from '@mui/icons-material/Description'
import GroupIcon from '@mui/icons-material/Group'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'

import {
    ProcessCtaKind,
    ProcessLeaseTerms,
    ProcessStatusCta,
} from '../../types/leasingContext'
import { getCurrency } from '../../helpers/stringHelpers'

interface ApplicantActionCardProps {
    cta: ProcessStatusCta
    onClick: () => void
    leaseTerms?: ProcessLeaseTerms | null
}

const formatLeaseDate = (value?: string | null): string | null => {
    if (!value) return null
    const dateOnly = value.slice(0, 10)
    const parts = dateOnly.split('-').map(Number)
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
        return value
    }
    const [year, month, day] = parts
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    })
}

const ACTION_COPY: Record<
    Exclude<ProcessCtaKind, 'review_offer'>,
    { title: string; Icon: typeof AssignmentIcon }
> = {
    continue_application: {
        title: 'Your Application',
        Icon: AssignmentIcon,
    },
    complete_screening: {
        title: 'Background screening',
        Icon: VerifiedUserIcon,
    },
    sign_document: {
        title: 'Lease packet',
        Icon: DescriptionIcon,
    },
    continue_lifestyle: {
        title: 'Roommate lifestyle profile',
        Icon: GroupIcon,
    },
}

export const ApplicantActionCard: React.FC<ApplicantActionCardProps> = ({
    cta,
    onClick,
    leaseTerms,
}) => {
    if (cta.kind === 'review_offer' || !cta.label) {
        return null
    }
    const copy = ACTION_COPY[cta.kind]
    const Icon = copy.Icon
    const showLeaseTerms =
        cta.kind === 'sign_document' &&
        Boolean(
            leaseTerms?.move_in_date ||
                leaseTerms?.move_out_date ||
                leaseTerms?.rent,
        )
    const termItems = showLeaseTerms
        ? [
              {
                  label: 'Move-in date',
                  value: formatLeaseDate(leaseTerms?.move_in_date),
              },
              {
                  label: 'Move-out date',
                  value: formatLeaseDate(leaseTerms?.move_out_date),
              },
              {
                  label: 'Monthly rent',
                  value: leaseTerms?.rent
                      ? getCurrency(leaseTerms.rent)
                      : null,
              },
          ].filter((item) => item.value)
        : []

    return (
        <Paper
            sx={{
                p: 3,
                mb: 3,
                backgroundColor: '#2a2f35',
                border: '1px solid',
                borderColor: 'grey.700',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                }}
            >
                <Icon sx={{ color: '#46b083' }} />
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                    {copy.title}
                </Typography>
            </Box>
            {termItems.length > 0 && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns:
                            'repeat(auto-fit, minmax(140px, 1fr))',
                        gap: 2,
                        mb: 2,
                    }}
                >
                    {termItems.map((item) => (
                        <Box key={item.label}>
                            <Typography
                                variant="caption"
                                sx={{ color: 'rgba(255,255,255,0.5)' }}
                            >
                                {item.label}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ color: '#fff', fontWeight: 600 }}
                            >
                                {item.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
            <Button
                variant="contained"
                onClick={onClick}
                sx={{
                    backgroundColor: '#008C85',
                    '&:hover': { backgroundColor: '#007c75' },
                }}
            >
                {cta.label}
            </Button>
        </Paper>
    )
}
